import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowLeft, ArrowsLeftRight, Buildings, CalendarBlank, ChartDonut, ChartPieSlice, Door, DoorOpen, House, LinkSimple, Plus, Ruler, Trash, Users, UsersThree, X,
} from '@phosphor-icons/react';
import {
  FeldRaster, ImmoButton, InnenKarte, KartenTitel, KpiReihe, KpiTile, PanelZeile, SchalterZeile, SearchSelect, SegmentedControl, TextFeld, ToastPill, useToastPill,
} from '../../../../../components/immobilien/ui';
import { eur } from '../../../../../lib/immobilien/format';
import { deDatum, mieterName } from '../../../../../lib/immobilien/objektDetail';
import { objektOptionen } from '../../../../../lib/immobilien/objektOptionen';
import { zuordnungsDaten, type ObjektRoh } from '../../../../../lib/eigentuemer/liste';
import { ROLLE_LANG, type Zuordnung } from '../../../../../lib/eigentuemer/rollen';
import {
  LEERER_EIG_ENTWURF, belegung, einheitenMitEigentum, objektArt, objektBesitzer, schreibPlan, wechselText, type EigentumsDaten, type WechselArt,
} from '../../../../../lib/eigentuemer/neuerEigentuemer';
import { schreibeZuordnung } from '../../../../../lib/eigentuemer/neuerEigentuemerSpeichern';
import { hebeZuweisungAuf } from '../../../../../lib/eigentuemer/aktionen';
import { aufhebenBisVorschlag } from '../../../../../lib/eigentuemer/stammdaten';
import { ladeEigentumImObjekt, ladeObjekteZurAuswahl } from '../../../../../lib/eigentuemer/verknuepfenDaten';
import { meldeEigentuemerGeaendert } from '../../../../../lib/eigentuemer/useEigentuemerDetail';
import { useEigSeite } from '../EigentuemerSeitenKontext';

const GLAS: React.CSSProperties = { background: 'var(--glass)' };
const fehlerText = (e: unknown) => (e instanceof Error ? e.message : 'Unbekannter Fehler');
const tausend = (n: number) => n.toLocaleString('de-DE', { maximumFractionDigits: 2 });

// ── Objekte & Rollen (Screens 34, 35) ──────────────────────────────────────────
export function ObjektePanel() {
  const s = useEigSeite();
  const navigate = useNavigate();
  const [toast, zeige] = useToastPill();
  const [verknuepfen, setVerknuepfen] = useState(false);
  const [aufheben, setAufheben] = useState<Zuordnung | null>(null);
  const [bis, setBis] = useState(() => aufhebenBisVorschlag(s.heute));
  const [laeuft, setLaeuft] = useState(false);
  const z = s.daten.zeile;
  const p = s.portfolio;
  const objekte = new Map(s.daten.objekte.map(o => [o.id, o]));
  const mieterJeEinheit = useMemo(() => zuordnungsDaten(s.daten.objekte, s.daten.einheiten, s.daten.mieter, s.heute).mieterJeEinheit, [s.daten, s.heute]);

  const hebeAuf = async () => {
    if (!aufheben) return;
    setLaeuft(true);
    try {
      await hebeZuweisungAuf(s.daten.eigentuemer.id, aufheben, z.zuordnungen, bis);
      zeige(`Zuweisung ${aufheben.objektName} aufgehoben – ${s.name} ist dort ${bis < s.heute ? 'ehemalig' : `bis ${deDatum(bis)} Eigentümer`}`);
      setAufheben(null);
      meldeEigentuemerGeaendert(s.daten.eigentuemer.id);
    } catch (err) {
      zeige(`Nicht aufgehoben: ${fehlerText(err)}`, true);
    } finally {
      setLaeuft(false);
    }
  };

  const einheiten = z.zuordnungen.flatMap(zo => zo.einheiten.map(e => ({ zo, e })));

  return (
    <>
      {toast && <ToastPill text={toast.text} fehler={toast.fehler} />}
      <KpiReihe>
        <KpiTile layout="kachel" label="Objekte" wert={p.objekte} icon={Buildings} ton="acc" />
        <KpiTile layout="kachel" label="Einheiten" wert={p.einheiten} icon={Door} ton="info" />
        <KpiTile layout="kachel" label="Fläche" wert={`${tausend(p.flaeche)} m²`} icon={Ruler} ton="warn" />
        {z.werte.verwaltet
          ? <KpiTile layout="kachel" label="Belegung (verwaltet)" wert={p.belegung === null ? '–' : `${p.belegung} %`} icon={ChartDonut} ton={p.belegung === 100 ? 'ok' : 'warn'} />
          : <KpiTile layout="kachel" label="MEA" wert={`${tausend(p.mea)} / 1.000`} icon={ChartPieSlice} ton="ok" />}
      </KpiReihe>

      <InnenKarte style={GLAS} padding="14px 12px 10px" gap={4}>
        <div className="flex items-center justify-between flex-wrap" style={{ gap: 8, padding: '0 6px 6px' }}>
          <KartenTitel>Objekte &amp; Rollen</KartenTitel>
          {!verknuepfen && <ImmoButton icon={Plus} onClick={() => setVerknuepfen(true)}>Objekt verknüpfen</ImmoButton>}
        </div>
        {z.zuordnungen.length === 0 && <span style={{ padding: '6px 8px 10px', fontSize: 13, color: 'var(--ink3)' }}>Noch keinem Objekt zugeordnet.</span>}
        {z.zuordnungen.map(zo => {
          const o = objekte.get(zo.objektId);
          const ort = (o?.stadt ?? '').trim() || (o?.ort ?? '').trim();
          const umfang = zo.ganzesObjekt ? 'ganzes Objekt' : zo.einheiten.length ? zo.einheiten.map(e => e.bezeichnung).join(', ') : 'ohne Einheiten';
          const flaeche = Math.round(zo.einheiten.reduce((a, e) => a + e.flaeche, 0));
          return (
            <div key={`${zo.objektId}|${zo.rolle}`} className="flex items-center min-w-0" style={{ gap: 4 }}>
              <div className="flex-1 min-w-0">
                <PanelZeile icon={zo.rolle === 'MV' ? Buildings : UsersThree} ton="acc" titel={[zo.objektName, ort].filter(Boolean).join(' · ')}
                  sub={[ROLLE_LANG[zo.rolle], umfang, flaeche ? `${tausend(flaeche)} m²` : '', zo.bis ? `endet ${deDatum(zo.bis)}` : ''].filter(Boolean).join(' · ')}
                  rechts={`${zo.einheiten.length} WE`} rechtsFarbe="var(--ink2)" chip={{ text: zo.rolle.replace('+', ' + '), ton: 'acc' }}
                  onClick={() => s.zumObjekt(zo.objektId)} ariaLabel={`${zo.objektName} öffnen`} />
              </div>
              <ImmoButton groesse={32} icon={Trash} aria-label={`Zuweisung ${zo.objektName} aufheben`} title="Zuweisung aufheben"
                onClick={() => { setAufheben(zo); setBis(aufhebenBisVorschlag(s.heute)); }} />
            </div>
          );
        })}
      </InnenKarte>

      {aufheben && (
        <InnenKarte style={{ ...GLAS, boxShadow: 'inset 0 0 0 2px var(--bad)' }}>
          <KartenTitel icon={Trash}>Zuweisung aufheben</KartenTitel>
          <span style={{ fontSize: 13, color: 'var(--ink2)', lineHeight: 1.5 }}>
            {s.name} wird bei <b>{aufheben.objektName}</b> ({aufheben.ganzesObjekt ? 'ganzes Objekt' : aufheben.einheiten.map(e => e.bezeichnung).join(', ') || 'Objekt'}) zum ehemaligen Eigentümer.
            Die Zuordnung wird beendet, nicht gelöscht – die Historie bleibt erhalten.
          </span>
          <FeldRaster minBreite={200}>
            <TextFeld label="Letzter Tag als Eigentümer" type="date" icon={CalendarBlank} wert={bis} onChange={setBis} />
          </FeldRaster>
          <div className="flex justify-end flex-wrap" style={{ gap: 8 }}>
            <ImmoButton onClick={() => setAufheben(null)}>Abbrechen</ImmoButton>
            <ImmoButton art="bad" icon={Trash} disabled={laeuft || !bis} onClick={() => { void hebeAuf(); }}>{laeuft ? 'Wird aufgehoben …' : 'Zuweisung aufheben'}</ImmoButton>
          </div>
        </InnenKarte>
      )}

      {verknuepfen && <Verknuepfen onFertig={meldung => { setVerknuepfen(false); if (meldung) { zeige(meldung); meldeEigentuemerGeaendert(s.daten.eigentuemer.id); } }} onFehler={m => zeige(m, true)} />}

      <InnenKarte style={GLAS} padding="14px 12px 10px" gap={4}>
        <div style={{ padding: '0 6px 6px' }}><KartenTitel>Einheiten</KartenTitel></div>
        {einheiten.length === 0 && <span style={{ padding: '6px 8px 10px', fontSize: 13, color: 'var(--ink3)' }}>Keine Einheiten zugeordnet.</span>}
        {einheiten.map(({ zo, e }) => {
          const verwaltet = zo.rolle !== 'WEG';
          const mieter = mieterJeEinheit.get(e.id) ?? [];
          const frei = verwaltet && !e.vermietet;
          const sub = [
            !verwaltet ? 'selbstgenutzt / nicht von uns vermietet' : frei ? 'Leerstand' : mieter.map(mieterName).join(', ') || 'vermietet',
            e.flaeche ? `${tausend(e.flaeche)} m²` : '',
            e.mea !== null ? `MEA ${tausend(e.mea)}/1.000` : '',
          ].filter(Boolean).join(' · ');
          return (
            <PanelZeile key={`${zo.objektId}|${e.id}`} icon={!verwaltet ? House : frei ? DoorOpen : Door} ton={!verwaltet ? 'info' : frei ? 'warn' : 'ok'}
              titel={`${e.bezeichnung} · ${zo.objektName}`} sub={sub}
              rechts={verwaltet && e.miete ? eur(e.miete, { nachkomma: 0 }) : '–'} rechtsFarbe={verwaltet && e.miete ? 'var(--ink)' : 'var(--ink3)'}
              chip={!verwaltet ? { text: 'nur WEG', ton: 'info' } : frei ? { text: 'frei', ton: 'warn' } : undefined}
              onClick={() => navigate(`/admin/objekte/${encodeURIComponent(zo.objektId)}/einheiten/${encodeURIComponent(e.id)}`)} ariaLabel={`${e.bezeichnung} öffnen`} />
          );
        })}
      </InnenKarte>
    </>
  );
}

// ── Objekt verknüpfen: Suche → Einheiten & Rolle → Stichtag, bei belegter Einheit Wechsel/Miteigentum ──
function Verknuepfen({ onFertig, onFehler }: { onFertig: (meldung: string | null) => void; onFehler: (m: string) => void }) {
  const s = useEigSeite();
  const eigId = s.daten.eigentuemer.id;
  const [objekte, setObjekte] = useState<ObjektRoh[] | null>(null);
  const [objektId, setObjektId] = useState<string | null>(null);
  const [daten, setDaten] = useState<EigentumsDaten | null>(null);
  const [gewaehlt, setGewaehlt] = useState<string[]>([]);
  const [sev, setSev] = useState(false);
  const [stichtag, setStichtag] = useState(s.heute);
  const [art, setArt] = useState<WechselArt>('wechsel');
  const [laeuft, setLaeuft] = useState(false);
  const fehlerRef = useRef(onFehler);
  fehlerRef.current = onFehler;
  // Mietverwaltung: nach dem Laden alle Einheiten vorwählen (wie „Neuer Eigentümer“)
  const vorwahl = useRef<string | null>(null);

  useEffect(() => {
    let aktiv = true;
    ladeObjekteZurAuswahl().then(o => { if (aktiv) setObjekte(o); }).catch((e: unknown) => fehlerRef.current(`Objekte nicht geladen: ${fehlerText(e)}`));
    return () => { aktiv = false; };
  }, []);
  useEffect(() => {
    if (!objektId) { setDaten(null); return; }
    let aktiv = true;
    setDaten(null);
    ladeEigentumImObjekt(objektId).then(d => {
      if (!aktiv) return;
      setDaten(d);
      if (vorwahl.current === objektId) { setGewaehlt(d.einheiten.map(e => e.id)); vorwahl.current = null; }
    }).catch((e: unknown) => fehlerRef.current(`Einheiten nicht geladen: ${fehlerText(e)}`));
    return () => { aktiv = false; };
  }, [objektId]);

  const optionen = useMemo(() => (objekte ? objektOptionen(objekte, new Map()) : []), [objekte]);
  const objekt = objekte?.find(o => o.id === objektId) ?? null;
  const oArt = objektArt(objekt?.verwaltungsart);
  const einheiten = useMemo(() => (objektId && daten ? einheitenMitEigentum(objektId, oArt, daten, s.heute) : []), [objektId, daten, oArt, s.heute]);
  // eigene laufende Einheiten sind schon zugeordnet; fremde zählen als Vorbesitzer
  const eigene = new Set(einheiten.filter(x => x.einheitBesitzer.some(b => b.id === eigId)).map(x => x.id));
  const fremd = useMemo(() => einheiten.map(x => ({ ...x, einheitBesitzer: x.einheitBesitzer.filter(b => b.id !== eigId), anzeige: x.anzeige.filter(b => b.id !== eigId) })), [einheiten, eigId]);
  const objektEig = useMemo(() => (objektId && daten ? objektBesitzer(objektId, daten, s.heute).filter(b => b.id !== eigId) : []), [objektId, daten, s.heute, eigId]);
  const b = belegung(oArt, fremd.filter(x => !eigene.has(x.id)), gewaehlt, objektEig);
  const rollen = oArt === 'WEG' ? (['WEG', 'WEG+SEV'] as const) : oArt === 'SEV' ? (['SEV'] as const) : (['MV'] as const);

  const waehle = (id: string) => {
    const o = objekte?.find(x => x.id === id);
    setObjektId(id);
    setSev(false);
    setArt('wechsel');
    setGewaehlt([]);
    vorwahl.current = objektArt(o?.verwaltungsart) === 'MV' ? id : null;
  };
  const schalte = (id: string) => setGewaehlt(g => (g.includes(id) ? g.filter(x => x !== id) : [...g, id]));

  const speichere = async () => {
    if (!objektId) return;
    const auswahl = gewaehlt.filter(id => !eigene.has(id));
    const entwurf = { ...LEERER_EIG_ENTWURF, objektId, einheitIds: auswahl, art, beginn: stichtag, sev };
    const plan = schreibPlan(entwurf, oArt, fremd.filter(x => !eigene.has(x.id)), b, daten?.einheitEig ?? [], s.heute);
    if (!plan) return;
    setLaeuft(true);
    try {
      const warnungen = await schreibeZuordnung(eigId, entwurf, plan);
      const teile = [`${objekt ? (objekt.bezeichnung ?? 'Objekt') : 'Objekt'} verknüpft (${plan.ebene === 'objekt' ? 'ganzes Objekt' : `${auswahl.length} WE`}, ${sev ? 'WEG + SEV' : rollen[0].replace('+', ' + ')})`];
      if (b.vorbesitzer.length && art === 'wechsel') teile.push('Wechsel vorgemerkt');
      onFertig([...teile, ...warnungen].join(' · '));
    } catch (err) {
      onFehler(`Nicht verknüpft: ${fehlerText(err)}`);
    } finally {
      setLaeuft(false);
    }
  };

  const ausgewaehlt = objekt ? optionen.find(o => o.id === objekt.id) ?? null : null;
  const anzahl = gewaehlt.filter(id => !eigene.has(id)).length;
  const bereit = !!objektId && !!stichtag && (einheiten.length === 0 || anzahl > 0);

  return (
    <InnenKarte style={{ ...GLAS, boxShadow: 'inset 0 0 0 2px var(--acc)' }}>
      <KartenTitel icon={LinkSimple}>{objekt ? `${objekt.bezeichnung ?? 'Objekt'} · Einheiten & Rolle` : 'Objekt verknüpfen'}</KartenTitel>
      <SearchSelect label="Objekt suchen" optionen={optionen} ausgewaehlt={ausgewaehlt} onWahl={o => waehle(o.id)} onEntfernen={() => setObjektId(null)}
        platzhalter={objekte ? 'Objektname oder Straße …' : 'Objekte werden geladen …'} maxTreffer={8} />
      {objekt && (
        <>
          <SegmentedControl<string> ariaLabel="Rolle" hoehe={34} className="self-start" wert={sev ? 'WEG+SEV' : rollen[0]}
            onChange={r => setSev(r === 'WEG+SEV')} segmente={rollen.map(r => ({ key: r, label: r.replace('+', ' + ') }))} />
          {!daten && <span style={{ fontSize: 13, color: 'var(--ink3)' }}>Einheiten werden geladen …</span>}
          {daten && einheiten.length === 0 && <span style={{ fontSize: 13, color: 'var(--ink3)' }}>Für dieses Objekt sind keine Einheiten angelegt – Zuordnung zum ganzen Objekt.</span>}
          {daten && einheiten.length > 0 && (
            <div className="grid" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(min(240px, 100%), 1fr))', gap: 8 }}>
              {fremd.map(x => {
                const schon = eigene.has(x.id);
                const besitz = x.anzeige.map(v => v.name).join(', ');
                return (
                  <SchalterZeile key={x.id} icon={Door} label={x.bezeichnung} an={schon || gewaehlt.includes(x.id)} disabled={schon} onChange={() => schalte(x.id)}
                    zusatz={schon ? 'bereits zugeordnet' : besitz ? `Eigentum ${besitz}` : 'ohne Eigentümer'} />
                );
              })}
            </div>
          )}
          <FeldRaster minBreite={200}>
            <TextFeld label="Stichtag" type="date" icon={CalendarBlank} wert={stichtag} onChange={setStichtag} />
          </FeldRaster>
          {b.vorbesitzer.length > 0 && (
            <div className="flex flex-col" style={{ gap: 6 }}>
              <span style={{ fontSize: 12, fontWeight: 700, color: 'var(--ink3)' }}>Bisheriger Eigentümer: {b.vorbesitzer.map(v => v.name).join(', ')}</span>
              <SegmentedControl<WechselArt> ariaLabel="Wechsel oder Miteigentum" hoehe={34} className="self-start" wert={art} onChange={setArt}
                segmente={[{ key: 'wechsel', label: 'Eigentümerwechsel', icon: ArrowsLeftRight }, { key: 'miteigentuemer', label: 'Miteigentümer', icon: Users }]} />
              <span style={{ fontSize: 12, color: 'var(--ink3)' }}>
                {art === 'wechsel' ? wechselText(b.vorbesitzer, stichtag) : 'Kommt hinzu, niemand gibt ab (z. B. Ehepartner, Erbengemeinschaft).'}
              </span>
            </div>
          )}
        </>
      )}
      <div className="flex justify-end flex-wrap" style={{ gap: 8 }}>
        {objekt ? <ImmoButton icon={ArrowLeft} onClick={() => setObjektId(null)}>Zurück</ImmoButton> : <ImmoButton icon={X} onClick={() => onFertig(null)}>Abbrechen</ImmoButton>}
        {objekt && (
          <ImmoButton art="akzent" icon={LinkSimple} disabled={laeuft || !bereit} onClick={() => { void speichere(); }}>
            {laeuft ? 'Wird verknüpft …' : 'Verknüpfen'}
          </ImmoButton>
        )}
      </div>
    </InnenKarte>
  );
}
